import type { ScriptLine } from "../../coldcompute";

// "Can You Trust an AI-Generated Diagram?" — source-diagram passthrough short.
// Cold Compute / Neon kit. Caption lines are SCENE-LOCAL (fromSec resets to 0
// inside each scene's <Sequence>). VO == on-screen caption.
//
// GROUNDED in EAKC-MVP (source-diagram passthrough shipped 2026-07-12, commit 2a8c15ea):
//   THE FIND — asked "give me the architecture diagram of the Action-Oriented Dynamic UI",
//     EAKC produced a confident mermaid diagram that PASSED the coverage grader ("there is a
//     diagram") but was the WRONG one: a mashup of the mission-step dispatch flow + the
//     draft-approval widget internals. The real architecture (one descriptor contract →
//     three surfaces → one action spine) was hand-drawn in the source doc and never made it in.
//   THE MECHANISM — RAG turns docs into text chunks; a diagram in a doc becomes TEXT. Ask for
//     a diagram and the model draws a NEW one from prose — it does not copy the real one. Worse:
//     if the diagram-bearing doc embeds poorly (an HTML page's markup dilutes its embedding),
//     it isn't even retrieved, so the model has nothing to copy from.
//   THE FIX — source-diagram passthrough: detect a diagram request, extract an existing mermaid
//     diagram from a source (fenced ```mermaid AND the <div class="mermaid"> HTML-VC form),
//     score it by query-term overlap, rescue it from the DB if the doc ranked poorly, and inject
//     it VERBATIM with a reproduce-exactly directive. Synthesis becomes the labeled fallback.
// HONEST LABEL: the passthrough directive strongly nudges the model to reproduce verbatim; a
//   fully-deterministic server-side render (bypassing the model for the diagram) is the named
//   next slice. No fabricated numbers.

export const HOOK: ScriptLine[] = [
  { scene: "hook", fromSec: 0.3, holdSec: 5.0, caption: "I asked my AI for the architecture diagram. It drew a clean, confident one — with boxes, arrows, a title." },
  { scene: "hook", fromSec: 5.5, holdSec: 4.5, caption: "The grader said yes, there's a diagram. Coverage: passed." },
  { scene: "hook", fromSec: 10.2, holdSec: 3.5, caption: "It was the wrong diagram. And nothing flagged it." },
];

export const REDRAW: ScriptLine[] = [
  { scene: "redraw", fromSec: 0.3, holdSec: 6, caption: "Here's the thing nobody tells you: a RAG chatbot doesn't reproduce your diagram. It REDRAWS it." },
  { scene: "redraw", fromSec: 6.5, holdSec: 6, caption: "Your docs become text chunks. A diagram in a doc becomes text. Ask for one, and the model draws a NEW diagram from prose." },
  { scene: "redraw", fromSec: 13, holdSec: 4.5, caption: "It never copies the real one sitting right there. It draws from memory — and memory omits and conflates." },
];

export const EVIDENCE: ScriptLine[] = [
  { scene: "evidence", fromSec: 0.3, holdSec: 6, caption: "Mine merged two unrelated subsystems and dropped the core: the descriptor contract, the three surfaces, the action spine." },
  { scene: "evidence", fromSec: 6.5, holdSec: 6, caption: "The grader only checks 'is there a diagram that addresses the question' — not 'is this diagram faithful.'" },
  { scene: "evidence", fromSec: 13, holdSec: 4.5, caption: "So a confident, wrong-but-plausible diagram sails right through. 'There is a diagram' is not 'the diagram is true.'" },
];

export const WORSE: ScriptLine[] = [
  { scene: "worse", fromSec: 0.3, holdSec: 6, caption: "And it gets worse. The real diagram lived in an HTML doc — whose markup dilutes its embedding." },
  { scene: "worse", fromSec: 6.5, holdSec: 5, caption: "So the doc that HAD the answer ranked poorly and wasn't even retrieved. The model had nothing to copy — so it invented." },
];

export const FIX: ScriptLine[] = [
  { scene: "fix", fromSec: 0.3, holdSec: 6, caption: "The fix: source-diagram passthrough. When the real diagram already exists in a source, surface THAT one — verbatim." },
  { scene: "fix", fromSec: 6.5, holdSec: 6, caption: "Don't paraphrase the blueprint from memory. Go find the actual blueprint in the drawer and reproduce it exactly." },
  { scene: "fix", fromSec: 13, holdSec: 6, caption: "Only when there genuinely is no source diagram do you fall back to synthesis — and you label it as generated." },
];

export const HOW: ScriptLine[] = [
  { scene: "how", fromSec: 0.3, holdSec: 6, caption: "How it works: detect a diagram request. Extract mermaid from sources — fenced blocks AND the div form the HTML docs use." },
  { scene: "how", fromSec: 6.5, holdSec: 6, caption: "Score by query-term overlap so it's the diagram you actually asked about. If the doc ranked poorly, a bounded DB search rescues it." },
  { scene: "how", fromSec: 13, holdSec: 4.5, caption: "Then inject it verbatim with a reproduce-exactly directive. Domain-agnostic, fail-safe, and it shows up in the trace." },
];

export const LESSON: ScriptLine[] = [
  { scene: "lesson", fromSec: 0.3, holdSec: 6, caption: "The rule: for anything that can exist authoritatively in a source — a diagram, a table, a config — reproduce ground truth." },
  { scene: "lesson", fromSec: 6.5, holdSec: 5.5, caption: "Synthesis is the fallback, not the default. An answer-shaped thing is not a faithful answer." },
];

export const OUTRO: ScriptLine[] = [
  { scene: "outro", fromSec: 0.3, holdSec: 5, caption: "A generated diagram looks authoritative. That's exactly why you can't trust it until it reproduces the real one." },
  { scene: "outro", fromSec: 5.5, holdSec: 3.8, caption: "It's all open — the repo, the extractor, the fix. Build in public, break in production." },
];

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
